import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ship, ShieldCheck, Truck, BadgeDollarSign, Handshake, Globe2, Download } from "lucide-react";
import api from "../lib/apiClient";
import { COMPANY } from "../data/site";
import { FaqAccordion } from "../components/FaqAccordion";
import { FAQS } from "../data/faq";

const HERO = "/assets/showcase/hero-bg.jpg";

const why = [
    { icon: BadgeDollarSign, title: "Competitive Wholesale Prices", text: "Direct sourcing keeps your margins strong with reliable case pricing." },
    { icon: Ship, title: "Reliable Import & Export", text: "Trusted global logistics moving premium goods from origin to your shelf." },
    { icon: Handshake, title: "Strong Customer Service", text: "A dedicated team that treats every retailer as a long-term partner." },
    { icon: ShieldCheck, title: "Quality & Trusted Brands", text: "Premium, exclusive brands with Halal & FDA certified products." },
];

export default function Home() {
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/brands").then((r) => setBrands(r.data));
        api.get("/settings").then((r) => setSettings(r.data));
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[88vh] flex items-center grain">
                <div className="absolute inset-0">
                    <img src={HERO} alt="YATTO LLC wholesale food, snack and beverage product showcase" className="w-full h-full object-cover object-[42%_center]" />
                    <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/65 to-foreground/25" />
                    <div className="absolute inset-0 bg-foreground/20" />
                </div>
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 w-full -translate-y-16 lg:-translate-y-24">
                    <div className="max-w-3xl text-background">
                        <p className="text-sm uppercase tracking-[0.3em] text-secondary mb-6 animate-fade-up">{COMPANY.tagline}</p>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98] mb-9 sm:mb-12 animate-fade-up" style={{ animationDelay: "0.1s" }}>
                            Wholesale Food, Drinks &amp; Snacks Distribution for Stores &amp; Businesses
                        </h1>
                        <div className="flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
                            <Link to="/catalog" data-testid="hero-view-catalog" className="group flex items-center gap-2 bg-secondary text-secondary-foreground px-8 py-4 font-semibold hover:-translate-y-1 transition-transform">
                                View Catalog <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link to="/order" data-testid="hero-place-order" className="flex items-center gap-2 bg-background/10 backdrop-blur-sm border border-background/30 text-background px-8 py-4 font-semibold hover:bg-background hover:text-foreground transition-colors">
                                Place Order Request
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-foreground/90 backdrop-blur text-background border-t border-background/10">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-background/10">
                        {[["10+", "Premium Brands"], ["48+", "Wholesale Products"], ["6", "Countries Sourced"], ["TX", "Distribution Hub"]].map(([n, l]) => (
                            <div key={l} className="py-6 px-4 text-center">
                                <div className="font-heading font-black text-2xl text-secondary">{n}</div>
                                <div className="text-xs uppercase tracking-wider text-background/60">{l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
                        <div>
                            <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">What We Distribute</p>
                            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight">Featured Categories</h2>
                        </div>
                        <Link to="/catalog" className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
                            Browse all products <ArrowRight size={18} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {categories.map((c) => (
                            <Link
                                key={c.id}
                                to={`/catalog?category=${encodeURIComponent(c.name)}`}
                                data-testid={`home-category-${c.id}`}
                                className="group relative aspect-[4/5] overflow-hidden bg-foreground"
                            >
                                <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-5">
                                    <h3 className="font-heading font-bold text-lg text-background leading-tight">{c.short || c.name}</h3>
                                    <span className="text-xs text-secondary flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">View products <ArrowRight size={12} /></span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* IMPORT / EXPORT */}
            <section className="relative bg-primary text-primary-foreground overflow-hidden">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-0 items-stretch">
                    <div className="py-24 sm:py-32 lg:pr-16">
                        <Globe2 size={40} className="text-secondary mb-6" />
                        <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-6">Your Global Partner in Quality</h2>
                        <p className="text-primary-foreground/80 leading-relaxed mb-6">
                            YATTO LLC specializes in the import, export, and wholesale distribution of premium-quality products. We connect trusted brands with the right markets through reliable sourcing, competitive pricing, and excellent customer service.
                        </p>
                        <div className="flex items-center gap-3 text-secondary mb-2"><Truck size={20} /> <span className="font-semibold">{COMPANY.serviceArea}</span></div>
                        <p className="text-primary-foreground/70 text-sm mb-8">
                            We supply wholesalers, retailers, grocery stores, gas stations, restaurants, supermarkets and business partners across Texas and nationwide.
                        </p>
                        <Link to="/about" className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground px-7 py-3.5 font-semibold hover:bg-background hover:text-foreground transition-colors">
                            About YATTO LLC <ArrowRight size={18} />
                        </Link>
                    </div>
                    <div className="relative min-h-[420px] lg:min-h-full overflow-hidden">
                        <img src="/assets/showcase/partner.jpg" alt="YATTO LLC products on premium retail shelves — olive oil, beverages, snacks, spices and grocery" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center" />
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE */}
            <section className="py-24 sm:py-32 bg-muted">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">The YATTO Advantage</p>
                    <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-14">Why Choose YATTO LLC</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
                        {why.map((w) => (
                            <div key={w.title} className="bg-background p-8 hover:bg-card transition-colors">
                                <w.icon size={32} className="text-primary mb-5" />
                                <h3 className="font-heading font-bold text-lg mb-2">{w.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{w.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BRANDS */}
            <section className="py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
                        <div>
                            <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">Trusted Partners</p>
                            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight">Featured Brands</h2>
                        </div>
                        <Link to="/brands" className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">All brands <ArrowRight size={18} /></Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
                        {brands
    .filter((b) => b.name?.trim().toLowerCase() !== "ima products")
    .map((b) => (
                            <div key={b.id} className="bg-background aspect-[3/2] flex flex-col items-center justify-center text-center p-6 hover:bg-primary hover:text-primary-foreground transition-colors group" data-testid={`home-brand-${b.id}`}>
                                <span className="font-heading font-black text-2xl tracking-tight">{b.name}</span>
                                <span className="text-xs uppercase tracking-wider text-muted-foreground group-hover:text-secondary mt-1">{b.origin}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 sm:py-32 bg-muted">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">Wholesale Questions</p>
                        <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight">Frequently Asked Questions</h2>
                    </div>
                    <FaqAccordion items={FAQS.slice(0, 6)} />
                    <div className="text-center mt-8">
                        <Link to="/faq" className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">View all FAQs <ArrowRight size={18} /></Link>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-foreground text-background">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-4">Ready to stock your shelves?</h2>
                        <p className="text-background/70 max-w-lg">Submit your wholesale order request and our team will confirm product availability, pricing, and delivery. No online payment required.</p>
                    </div>
                    <div className="flex flex-wrap gap-4 lg:justify-end">
                        {settings?.catalog_pdf && (
                            <a href={settings.catalog_pdf} download={settings.catalog_filename || "YATTO-Catalog.pdf"} data-testid="home-download-catalog" className="flex items-center gap-2 bg-background/10 border border-background/30 text-background px-7 py-4 font-semibold hover:bg-background hover:text-foreground transition-colors">
                                <Download size={18} /> Download Catalog
                            </a>
                        )}
                        <Link to="/order" className="flex items-center gap-2 bg-secondary text-secondary-foreground px-8 py-4 font-semibold hover:-translate-y-1 transition-transform">
                            Place Order Request <ArrowRight size={18} />
                        </Link>
                        <Link to="/contact" className="flex items-center gap-2 border border-background/30 text-background px-8 py-4 font-semibold hover:bg-background hover:text-foreground transition-colors">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
